import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PageHeader } from "@/components/layout/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
    RefreshCw, Play, Square, CalendarDays, AlertTriangle, CheckCircle2,
    Clock, Loader2, Info, PlugZap, ShieldAlert, Boxes, PhoneCall, Store, CalendarClock,
    Receipt, CloudUpload, Eye,
} from "lucide-react";
import { OPS } from "@/constants/testIds";
import { fmtInt, isoFromDate, dateFromIso } from "@/lib/format";

const SS = OPS.salesSync;
const POLL_MS = 2500;
// mirrors pos_sync_service.SETTLE_MARGIN: a pull before (day + 1) 02:00 Riyadh is a snapshot
const SETTLE_HOURS = 2;

// Business days are Asia/Riyadh — the device clock (often another zone) must not
// decide which day is "yesterday": a pull that includes today stores a partial day.
function daysAgoIso(n) {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date()).map((x) => [x.type, x.value]));
    return new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day) - n)).toISOString().slice(0, 10);
}
function yesterdayIso() {
    return daysAgoIso(1);
}
// pulled before the day had settled (Riyadh has no DST: a fixed +03:00)
function pulledEarly(dayIso, pulledAt) {
    if (!dayIso || !pulledAt) return false;
    const settle = Date.parse(`${dayIso}T00:00:00+03:00`) + (24 + SETTLE_HOURS) * 3600 * 1000;
    const at = Date.parse(pulledAt);
    return Number.isFinite(at) && at < settle;
}
function fmtDateTime(iso, lang) {
    if (!iso) return "\u2014";
    try {
        return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", {
            year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

// ---------------------------------------------------------------- date field
function DateField({ testId, label, value, onChange, disabled, maxDate }) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const parse = (s) => {
        if (!s) return undefined;
        const [y, m, d] = s.split("-").map(Number);
        return new Date(y, m - 1, d);
    };
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[color:var(--ink-muted)]">{label}</Label>
            <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
                <PopoverTrigger asChild>
                    <Button
                        data-testid={testId}
                        variant="outline"
                        disabled={disabled}
                        className="w-full justify-start gap-2 surface border-[color:var(--border-soft)] font-normal"
                    >
                        <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)]" />
                        <bdi dir="ltr">{value || t("ss_pick_date")}</bdi>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={parse(value)}
                        disabled={maxDate ? { after: parse(maxDate) } : undefined}
                        onSelect={(d) => { if (d) onChange(isoFromDate(d)); setOpen(false); }}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}

function SummaryCard({ k, label, value, color, Icon, lang }) {
    return (
        <div
            data-testid={SS.summaryCard(k)}
            className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3"
        >
            <div className="flex items-center gap-1.5 text-xs text-[color:var(--ink-muted)]">
                {Icon ? <Icon className="h-3.5 w-3.5" /> : null}{label}
            </div>
            <p className="kpi-number text-2xl font-extrabold tnum" style={{ color: color || "var(--ink)" }}>
                <bdi dir="ltr">{fmtInt(value ?? 0, lang)}</bdi>
            </p>
        </div>
    );
}

const DAY_STATUS = {
    done: { color: "#16A34A", bg: "rgba(22,163,74,0.12)", Icon: CheckCircle2, key: "ss_status_done" },
    partial: { color: "#B45309", bg: "rgba(180,83,9,0.12)", Icon: AlertTriangle, key: "ss_status_partial" },
    provisional: { color: "var(--brand-brown)", bg: "rgba(209,128,47,0.14)", Icon: Clock, key: "ss_status_provisional" },
};
function ReconCell({ row, t, lang }) {
    if (row.reconciled == null) return <span className="text-xs text-[color:var(--ink-muted)]">—</span>;
    // `recon` (the invoice totals) is only sent to the super admin
    const c = row.recon;
    const tip = c ? `${t("ss_recon_branches")}: ${fmtInt(c.branch_inv, lang)} / ${t("ss_recon_network")}: ${fmtInt(c.network_inv, lang)}` : undefined;
    return (
        <span
            title={tip}
            className={`inline-flex items-center gap-1 text-xs font-semibold ${row.reconciled ? "text-[color:#16A34A]" : "text-[color:var(--danger)]"}`}
        >
            {row.reconciled ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {row.reconciled ? t("ss_recon_ok") : t("ss_recon_off")}
            {row.zero_branches?.length ? (
                <span className="text-[color:var(--ink-faint)]">· {t("ss_recon_zero")} {row.zero_branches.length}</span>
            ) : null}
        </span>
    );
}

// one layer's pull time; amber when it ran before the day had settled
function PulledAt({ label, day, at, t, lang }) {
    const early = pulledEarly(day, at);
    return (
        <div className={early ? "font-semibold text-[color:#B45309]" : undefined} title={early ? t("ss_pulled_early") : undefined}>
            <span className="text-[color:var(--ink-faint)]">{label}: </span>{fmtDateTime(at, lang)}
            {early ? <AlertTriangle className="ms-1 inline h-3 w-3" /> : null}
        </div>
    );
}

function DayStatusBadge({ status, t }) {
    const m = DAY_STATUS[status] || DAY_STATUS.partial;
    const Icon = m.Icon;
    return (
        <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold"
            style={{ color: m.color, backgroundColor: m.bg }}>
            <Icon className="h-3.5 w-3.5" />{t(m.key)}
        </span>
    );
}

// ------------------------------------------------- history backfill (long pull)
const HIST_STATE = {
    running: { color: "var(--brand-brown)", bg: "rgba(209,128,47,0.14)", Icon: Loader2, spin: true, key: "ss_hist_running" },
    stalled: { color: "var(--danger)", bg: "rgba(180,35,24,0.10)", Icon: AlertTriangle, key: "ss_hist_stalled" },
    idle: { color: "#B45309", bg: "rgba(180,83,9,0.12)", Icon: Clock, key: "ss_hist_paused" },
    complete: { color: "#16A34A", bg: "rgba(22,163,74,0.12)", Icon: CheckCircle2, key: "ss_hist_complete" },
};

function HistoryBackfillCard({ hist, active, onDone, readOnly }) {
    const { t, lang } = useI18n();
    const [busy, setBusy] = useState(false);
    if (!hist?.exists) return null;

    const m = HIST_STATE[hist.state] || HIST_STATE.idle;
    const canResume = !readOnly && !active && hist.days_remaining > 0;

    const resume = async () => {
        setBusy(true);
        try {
            const r = await api.post("/sales-sync/resume-history");
            toast.success(t("ss_hist_resumed").replace("{n}", fmtInt(r.data.days_remaining, lang)));
            await onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("ss_start_failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card data-testid={SS.history} className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
            <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <CardTitle className="text-lg font-bold text-[color:var(--ink)]">{t("ss_hist_title")}</CardTitle>
                    <span data-testid={SS.historyState}
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold"
                        style={{ color: m.color, backgroundColor: m.bg }}>
                        <m.Icon className={`h-3.5 w-3.5 ${m.spin ? "animate-spin" : ""}`} />{t(m.key)}
                    </span>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <p className="text-sm text-[color:var(--ink-muted)]">
                    {t("ss_hist_desc")} <bdi dir="ltr">{hist.from} → {hist.to}</bdi>
                </p>

                {hist.state === "stalled" ? (
                    <div className="flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5"
                        style={{ borderColor: "rgba(180,35,24,0.35)", backgroundColor: "rgba(180,35,24,0.06)" }}>
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--danger)]" />
                        <p className="text-xs leading-relaxed text-[color:var(--ink-muted)]">
                            {t("ss_hist_stalled_hint").replace("{n}", fmtInt(hist.no_progress_attempts, lang))}
                        </p>
                    </div>
                ) : null}

                {/* the state is this run's own; another pull holding the runner is said apart */}
                {hist.other_run_active && hist.state !== "complete" ? (
                    <p data-testid={SS.historyOtherRun}
                        className="flex items-center gap-1.5 text-xs text-[color:var(--ink-muted)]">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--brand-brown)]" />
                        {t("ss_hist_other_run")}
                    </p>
                ) : null}

                <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-[color:var(--ink-muted)]">
                        <span>{t("ss_hist_progress")}</span>
                        <span className="tnum">
                            <bdi dir="ltr">{fmtInt(hist.days_done, lang)} / {fmtInt(hist.days_total, lang)} {t("ss_days")} · {hist.pct}%</bdi>
                        </span>
                    </div>
                    <Progress data-testid={SS.historyBar} value={hist.pct} className="h-2" />
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <SummaryCard k="hist-done" label={t("ss_hist_days_done")} value={hist.days_done} color="#16A34A" lang={lang} />
                    <SummaryCard k="hist-remaining" label={t("ss_hist_days_left")} value={hist.days_remaining}
                        color={hist.days_remaining ? "#B45309" : "#16A34A"} lang={lang} />
                    <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_hist_eta")}</p>
                        <p className="text-sm font-semibold text-[color:var(--ink)] tnum">
                            <bdi dir="ltr">{hist.days_remaining ? `~${hist.eta_hours} h` : "\u2014"}</bdi>
                        </p>
                    </div>
                    <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_hist_last_try")}</p>
                        <p className="text-sm font-semibold text-[color:var(--ink)]">{fmtDateTime(hist.last_started_at, lang)}</p>
                    </div>
                </div>

                {canResume ? (
                    <Button data-testid={SS.historyResume} onClick={resume} disabled={busy}
                        className="gap-2 bg-[color:var(--brand-orange)] text-white hover:bg-[color:var(--brand-orange-hover)]">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        {t("ss_hist_resume")}
                    </Button>
                ) : null}
            </CardContent>
        </Card>
    );
}

// ---------------------------------------------------------------- page
export default function SalesSyncPage() {
    // Radix Tabs / ScrollArea stamp dir="ltr" unless told (no DirectionProvider)
    const { t, lang, dir } = useI18n();
    // system admins (it_admin) may watch; start / stop / resume are super-admin only
    const { isSuperAdmin } = useAuth();
    const readOnly = !isSuperAdmin;
    const maxDay = yesterdayIso();

    const [config, setConfig] = useState(null);
    const [run, setRun] = useState(null);
    const [active, setActive] = useState(false);
    const [coverage, setCoverage] = useState(null);

    const [mode, setMode] = useState("yesterday");   // yesterday | range
    const [singleDate, setSingleDate] = useState(yesterdayIso());
    const [fromDate, setFromDate] = useState(daysAgoIso(7));
    const [toDate, setToDate] = useState(yesterdayIso());
    const [incremental, setIncremental] = useState(true);
    const [datasets, setDatasets] = useState("both");
    const [starting, setStarting] = useState(false);

    const pollRef = useRef(null);

    const loadStatus = useCallback(async () => {
        try {
            const [s, cov] = await Promise.all([
                api.get("/sales-sync/status"),
                api.get("/sales-sync/coverage"),
            ]);
            setRun(s.data.run || null);
            setActive(!!s.data.active);
            setCoverage(cov.data || null);
            return !!s.data.active;
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        api.get("/sales-sync/config").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
        loadStatus();
    }, [loadStatus]);

    // poll while a run is active
    useEffect(() => {
        if (!active) {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        if (pollRef.current) return;
        pollRef.current = setInterval(async () => {
            const stillActive = await loadStatus();
            if (!stillActive && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }, POLL_MS);
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [active, loadStatus]);

    const handleStart = async () => {
        const payload = mode === "yesterday"
            ? { date_from: singleDate, date_to: singleDate }
            : { date_from: fromDate, date_to: toDate };
        if (mode === "range") {
            const a = dateFromIso(fromDate), b = dateFromIso(toDate);
            if (!a || !b || a > b) { toast.error(t("ss_invalid_range")); return; }
        }
        payload.mode = incremental ? "incremental" : "all";
        payload.datasets = datasets;
        setStarting(true);
        try {
            await api.post("/sales-sync/run", payload);
            toast.success(t("ss_started_toast"));
            setActive(true);
            await loadStatus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("ss_start_failed"));
        } finally {
            setStarting(false);
        }
    };

    const handleStop = async () => {
        try {
            await api.post("/sales-sync/cancel");
            toast.message(t("ss_cancelling"));
            await loadStatus();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("ss_start_failed"));
        }
    };

    const progress = run?.progress || {};
    const totals = run?.totals || {};
    const failures = run?.failures || [];
    const totalDays = progress.total_days || 0;
    const doneDays = progress.done_days || 0;
    const progressPct = totalDays ? Math.round((doneDays / totalDays) * 100) : (active ? 5 : 0);
    const notConfigured = config && !config.configured;

    const runStateBadge = useMemo(() => {
        if (active) return { text: t("ss_state_running"), color: "var(--brand-brown)", bg: "rgba(209,128,47,0.14)", Icon: Loader2, spin: true };
        if (!run) return null;
        if (run.status === "done") return { text: t("ss_state_done"), color: "#16A34A", bg: "rgba(22,163,74,0.12)", Icon: CheckCircle2 };
        if (run.status === "partial") return { text: t("ss_status_partial"), color: "#B45309", bg: "rgba(180,83,9,0.12)", Icon: AlertTriangle };
        if (run.status === "cancelled") return { text: t("ss_state_cancelled"), color: "var(--ink-muted)", bg: "rgba(90,82,77,0.10)", Icon: Square };
        if (run.status === "failed") return { text: t("ss_state_failed"), color: "#DC2626", bg: "rgba(220,38,38,0.10)", Icon: AlertTriangle };
        if (run.status === "interrupted") return { text: t("ss_state_interrupted"), color: "#B45309", bg: "rgba(180,83,9,0.12)", Icon: AlertTriangle };
        return null;
    }, [active, run, t]);

    return (
        <div data-testid={SS.page} className="fin-scope space-y-5 sm:space-y-6">
            <PageHeader icon={RefreshCw} title={t("ss_title")} subtitle={t("ss_subtitle")} />

            {/* connection / how it works */}
            <div
                data-testid={SS.configBanner}
                className="flex items-start gap-3 rounded-2xl border px-4 py-3"
                style={notConfigured
                    ? { borderColor: "var(--danger)", backgroundColor: "rgba(180,35,24,0.08)", color: "var(--danger)" }
                    : { borderColor: "var(--border-soft)", backgroundColor: "rgba(22,163,74,0.06)" }}
            >
                {notConfigured ? <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> : <PlugZap className="mt-0.5 h-5 w-5 shrink-0 text-[color:#16A34A]" />}
                <div className="space-y-0.5">
                    <p className="text-sm font-semibold" style={notConfigured ? {} : { color: "var(--ink)" }}>
                        {notConfigured ? t("ss_not_configured") : t("ss_connected")}
                    </p>
                    {!notConfigured ? (
                        <p className="text-xs text-[color:var(--ink-muted)]">
                            {t("ss_nightly_note")} <bdi dir="ltr">05:00</bdi>
                        </p>
                    ) : null}
                </div>
            </div>

            <Card className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                        <Info className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--brand-brown)]" />
                        <div className="space-y-1">
                            <p className="text-sm font-semibold text-[color:var(--ink)]">{t("ss_how_title")}</p>
                            <ul className="list-disc space-y-0.5 ps-5 text-sm text-[color:var(--ink-muted)]">
                                <li>{t("ss_how_1")}</li>
                                <li>{t("ss_how_2")}</li>
                                <li>{t("ss_how_3")}</li>
                            </ul>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* controls */}
            <Card className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <CardTitle className="text-lg font-bold text-[color:var(--ink)]">{t("ss_pull_title")}</CardTitle>
                        {runStateBadge ? (
                            <span className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold"
                                style={{ color: runStateBadge.color, backgroundColor: runStateBadge.bg }}>
                                <runStateBadge.Icon className={`h-3.5 w-3.5 ${runStateBadge.spin ? "animate-spin" : ""}`} />
                                {runStateBadge.text}
                            </span>
                        ) : null}
                    </div>
                </CardHeader>
                <CardContent className="space-y-5">
                    {readOnly ? (
                        <div data-testid={SS.readOnly}
                            className="flex items-start gap-2.5 rounded-xl border border-[color:var(--border-soft)] surface-2 px-3.5 py-2.5 max-w-lg">
                            <Eye className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-brown)]" />
                            <p className="text-xs leading-relaxed text-[color:var(--ink-muted)]">{t("ss_read_only")}</p>
                        </div>
                    ) : (<>
                    <Tabs value={mode} onValueChange={(v) => !active && setMode(v)} dir={dir}>
                        <TabsList data-testid={SS.modeTabs} className="grid w-full max-w-md grid-cols-2">
                            <TabsTrigger data-testid={SS.modeYesterday} value="yesterday" disabled={active}>{t("ss_mode_yesterday")}</TabsTrigger>
                            <TabsTrigger data-testid={SS.modeRange} value="range" disabled={active}>{t("ss_mode_range")}</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {mode === "yesterday" ? (
                        <div className="max-w-xs">
                            <DateField testId={SS.fromDate} label={t("ss_date_label")} value={singleDate} onChange={setSingleDate} disabled={active} maxDate={maxDay} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                            <DateField testId={SS.fromDate} label={t("ss_from")} value={fromDate} onChange={setFromDate} disabled={active} maxDate={maxDay} />
                            <DateField testId={SS.toDate} label={t("ss_to")} value={toDate} onChange={setToDate} disabled={active} maxDate={maxDay} />
                        </div>
                    )}

                    <div className="max-w-lg space-y-2">
                        <p className="text-sm font-medium text-[color:var(--ink)]">{t("ss_datasets_label")}</p>
                        <Tabs value={datasets} onValueChange={(v) => !active && setDatasets(v)} dir={dir}>
                            <TabsList data-testid={SS.datasetTabs} className="grid w-full grid-cols-3">
                                <TabsTrigger data-testid={SS.dataset("both")} value="both" disabled={active}>{t("ss_ds_both")}</TabsTrigger>
                                <TabsTrigger data-testid={SS.dataset("core")} value="core" disabled={active}>{t("ss_ds_core")}</TabsTrigger>
                                <TabsTrigger data-testid={SS.dataset("deep")} value="deep" disabled={active}>{t("ss_ds_deep")}</TabsTrigger>
                            </TabsList>
                        </Tabs>
                        <p className="text-xs leading-relaxed text-[color:var(--ink-muted)]">{t("ss_ds_hint")}</p>
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3 max-w-lg">
                        <div>
                            <p className="text-sm font-medium text-[color:var(--ink)]">{t("ss_incremental_title")}</p>
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_incremental_desc")}</p>
                        </div>
                        <Switch data-testid={SS.incrementalToggle} checked={incremental} onCheckedChange={setIncremental} disabled={active} />
                    </div>
                    </>)}

                    <div className="flex flex-wrap items-center gap-3">
                        {readOnly ? null : active ? (
                            <Button data-testid={SS.stop} onClick={handleStop} variant="outline"
                                className="gap-2 border-[color:var(--danger)] text-[color:var(--danger)] hover:bg-[color:rgba(180,35,24,0.08)]">
                                <Square className="h-4 w-4" />{t("ss_stop")}
                            </Button>
                        ) : (
                            <Button data-testid={SS.start} onClick={handleStart} disabled={starting || notConfigured}
                                className="gap-2 bg-[color:var(--brand-orange)] text-white hover:bg-[color:var(--brand-orange-hover)]">
                                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}{t("ss_start")}
                            </Button>
                        )}
                        {active && (progress.current_date || progress.current_branch) ? (
                            <span data-testid={SS.currentInfo} className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-muted)]">
                                <Loader2 className="h-4 w-4 animate-spin text-[color:var(--brand-brown)]" />
                                <bdi dir="ltr">{progress.current_date}</bdi>
                                {progress.current_branch ? <span>· {progress.current_branch}</span> : null}
                            </span>
                        ) : null}
                    </div>

                    {run ? (
                        <div className="space-y-4">
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-xs text-[color:var(--ink-muted)]">
                                    <span>{t("ss_progress")}</span>
                                    <span className="tnum"><bdi dir="ltr">{fmtInt(doneDays, lang)} / {fmtInt(totalDays, lang)} {t("ss_days")}</bdi></span>
                                </div>
                                <Progress data-testid={SS.progressBar} value={progressPct} className="h-2" />
                            </div>
                            <div data-testid={SS.summary} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <SummaryCard k="branches" label={t("ss_sum_branch_rows")} value={totals.branch_rows} Icon={Store} color="#16A34A" lang={lang} />
                                <SummaryCard k="items" label={t("ss_sum_item_days")} value={totals.item_days} Icon={Boxes} color="var(--brand-brown)" lang={lang} />
                                <SummaryCard k="cc" label={t("ss_sum_cc_orders")} value={totals.cc_orders} Icon={PhoneCall} color="var(--brand-brown)" lang={lang} />
                                <SummaryCard k="deep-orders" label={t("ss_sum_deep_orders")} value={totals.deep_orders} Icon={Receipt} color="var(--brand-orange)" lang={lang} />
                                <SummaryCard k="deep-items" label={t("ss_sum_deep_items")} value={totals.deep_item_rows} Icon={Boxes} color="var(--brand-orange)" lang={lang} />
                                <SummaryCard k="archived" label={t("ss_sum_archived")} value={totals.raw_archived} Icon={CloudUpload}
                                    color={totals.raw_archive_failed ? "#B45309" : "#16A34A"} lang={lang} />
                                <SummaryCard k="partial" label={t("ss_sum_partial")} value={totals.days_partial} Icon={AlertTriangle} color={totals.days_partial ? "#B45309" : "var(--ink)"} lang={lang} />
                                {progress.current_stage ? (
                                    <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
                                        <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_stage")}</p>
                                        <p className="text-sm font-semibold text-[color:var(--ink)]">
                                            {t(`ss_stage_${progress.current_stage}`)}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <div data-testid={SS.idle}
                            className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[color:var(--border-soft)] py-10 text-center text-[color:var(--ink-muted)]">
                            <RefreshCw className="h-7 w-7 text-[color:var(--brand-orange)]" />
                            <p className="text-sm">{t("ss_idle_hint")}</p>
                        </div>
                    )}

                    {failures.length ? (
                        <div data-testid={SS.failures} className="rounded-xl border border-[color:rgba(180,83,9,0.35)] bg-[color:rgba(180,83,9,0.06)] p-3">
                            <p className="mb-1.5 text-sm font-semibold text-[color:#B45309]">{t("ss_failures_title")} ({fmtInt(failures.length, lang)})</p>
                            <ScrollArea className="max-h-40" dir={dir}>
                                <ul className="space-y-1 text-xs text-[color:var(--ink-muted)]">
                                    {failures.slice(-40).reverse().map((f, i) => (
                                        <li key={i}>
                                            <bdi dir="ltr">{f.date || "\u2014"}</bdi>
                                            {f.branch ? <span> · {f.branch}</span> : null} — {f.reason || f.error}
                                        </li>
                                    ))}
                                </ul>
                            </ScrollArea>
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            {/* history backfill */}
            <HistoryBackfillCard hist={coverage?.history} active={active} onDone={loadStatus} readOnly={readOnly} />

            {/* coverage */}
            <Card className="surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)]">
                <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <CardTitle className="text-lg font-bold text-[color:var(--ink)]">{t("ss_coverage_title")}</CardTitle>
                        <Badge variant="secondary" className="gap-1.5 bg-[color:rgba(186,217,235,0.5)] text-[color:var(--brand-brown)] border-0">
                            <CalendarClock className="h-3.5 w-3.5" />
                            {t("ss_last_sync")}: {fmtDateTime(coverage?.last_sync_at, lang)}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                        <SummaryCard k="cov-done" label={t("ss_cov_done")} value={coverage?.done_days} color="#16A34A" lang={lang} />
                        <SummaryCard k="cov-deep" label={t("ss_cov_deep_done")} value={coverage?.deep_done_days} color="var(--brand-orange)" lang={lang} />
                        <SummaryCard k="cov-partial" label={t("ss_cov_partial")} value={coverage?.partial_days} color={coverage?.partial_days ? "#B45309" : "var(--ink)"} lang={lang} />
                        <SummaryCard k="cov-deep-partial" label={t("ss_cov_deep_partial")} value={coverage?.deep_partial_days} color={coverage?.deep_partial_days ? "#B45309" : "var(--ink)"} lang={lang} />
                        <SummaryCard k="cov-provisional" label={t("ss_cov_provisional")} value={coverage?.provisional_days} color={coverage?.provisional_days ? "var(--brand-brown)" : "var(--ink)"} lang={lang} />
                        <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_cov_first")}</p>
                            <p className="text-sm font-semibold text-[color:var(--ink)] tnum"><bdi dir="ltr">{coverage?.first_day || "\u2014"}</bdi></p>
                        </div>
                        <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
                            <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_cov_last")}</p>
                            <p className="text-sm font-semibold text-[color:var(--ink)] tnum"><bdi dir="ltr">{coverage?.last_day || "\u2014"}</bdi></p>
                        </div>
                    </div>

                    {coverage?.recent?.length ? (
                        // min-w + a horizontal bar: at 375 px the last column («وقت السحب») was cut off
                        <ScrollArea className="h-[300px] w-full" dir={dir}>
                            <Table data-testid={SS.coverage} className="min-w-[640px]">
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead className="text-start text-[color:var(--ink-muted)]">{t("ss_col_date")}</TableHead>
                                        <TableHead className="text-start text-[color:var(--ink-muted)]">{t("ss_col_status")}</TableHead>
                                        <TableHead className="text-start text-[color:var(--ink-muted)]">{t("ss_col_deep")}</TableHead>
                                        <TableHead className="text-start text-[color:var(--ink-muted)]">{t("ss_col_recon")}</TableHead>
                                        <TableHead className="text-start text-[color:var(--ink-muted)]">{t("ss_col_pulled_at")}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {coverage.recent.map((r) => (
                                        <TableRow key={r.date} data-testid={SS.coverageRow(r.date)}>
                                            <TableCell className="text-[color:var(--ink)] tnum"><bdi dir="ltr">{r.date}</bdi></TableCell>
                                            <TableCell><DayStatusBadge status={r.status} t={t} /></TableCell>
                                            <TableCell>{r.deep_status ? <DayStatusBadge status={r.deep_status} t={t} /> : <span className="text-xs text-[color:var(--ink-muted)]">—</span>}</TableCell>
                                            <TableCell data-testid={`sales-sync-recon-${r.date}`}>
                                                <ReconCell row={r} t={t} lang={lang} />
                                            </TableCell>
                                            <TableCell className="text-xs text-[color:var(--ink-muted)]">
                                                <PulledAt label={t("ss_ds_core")} day={r.date} at={r.pulled_at} t={t} lang={lang} />
                                                {r.deep_pulled_at ? <PulledAt label={t("ss_ds_deep")} day={r.date} at={r.deep_pulled_at} t={t} lang={lang} /> : null}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    ) : (
                        <p className="py-6 text-center text-sm text-[color:var(--ink-muted)]">{t("ss_cov_empty")}</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
